/**
 * The single shared AudioContext.
 *
 * Browsers refuse to start audio before a user gesture, so the context is created lazily and
 * `unlockOnGesture()` resumes it on the first real interaction. Every audio module in the game
 * pulls the context from here — one context, one clock, one output device.
 */
let context: AudioContext | null = null;
const readyListeners = new Set<(running: boolean) => void>();

export function audioContext(): AudioContext {
  if (!context) {
    const Ctor: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    context = new Ctor({ latencyHint: 'interactive' });
    context.onstatechange = () => notify();
  }
  return context;
}

export function isRunning(): boolean {
  return context?.state === 'running';
}

function notify(): void {
  const running = isRunning();
  for (const listener of readyListeners) listener(running);
}

export function onAudioStateChange(listener: (running: boolean) => void): () => void {
  readyListeners.add(listener);
  return () => readyListeners.delete(listener);
}

export async function resumeAudio(): Promise<boolean> {
  const ctx = audioContext();
  if (isRunning()) return true;
  try {
    await ctx.resume();
  } catch {
    /* still blocked — the caller shows the "click to enable" affordance */
  }
  notify();
  return isRunning();
}

/**
 * Resume the context on the first user gesture anywhere on the page. Autoplay policy means the
 * landing ambience genuinely cannot start before this happens.
 */
export function unlockOnGesture(): void {
  const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart'];
  const handler = (): void => {
    void resumeAudio().then((running) => {
      if (running) for (const event of events) window.removeEventListener(event, handler);
    });
  };
  for (const event of events) window.addEventListener(event, handler, { passive: true });
}

/** Click-free parameter moves: always ramp, never assign. */
export function rampTo(param: AudioParam, value: number, seconds = 0.05): void {
  const ctx = audioContext();
  const now = ctx.currentTime;
  param.cancelScheduledValues(now);
  param.setValueAtTime(param.value, now);
  param.linearRampToValueAtTime(value, now + Math.max(seconds, 0.005));
}
